import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inpatientAPI } from '../../../../services/api';
import { showErrorToast, showSuccessToast } from '../../../../utils/notificationUtils';

const AccommodationTab = forwardRef(({ inpatientId, initialData, onSave }, ref) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // --- State ---
    const [isIsolated, setIsIsolated] = useState(false);

    // Standard Mode State
    const [standardCage, setStandardCage] = useState('');
    const [standardDuration, setStandardDuration] = useState(0);

    // Isolated Mode State
    const [isolationCage, setIsolationCage] = useState('');
    const [isolationDuration, setIsolationDuration] = useState(0);
    const [hasPostIsolation, setHasPostIsolation] = useState(false);
    const [postIsolationCage, setPostIsolationCage] = useState('');
    const [postIsolationDuration, setPostIsolationDuration] = useState(0);

    // --- Initialization ---
    useEffect(() => {
        if (initialData) {
            initialize(initialData);
        } else {
            fetchData();
        }
    }, [inpatientId, initialData]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await inpatientAPI.getInpatientVisit(inpatientId);
            if (response.data && response.data.data) {
                initialize(response.data.data);
            }
        } catch (error) {
            console.error(error);
            showErrorToast(t('common.errorLoadingData'));
        } finally {
            setLoading(false);
        }
    };

    const initialize = (data) => {
        const isolated = !!data.is_isolated;
        setIsIsolated(isolated);

        // Try to read specific details from new JSON column
        let details = data.accommodation_details || null;

        // Handle stringified JSON if API returns it as string
        if (typeof details === 'string') {
            try { details = JSON.parse(details); } catch (e) { }
        }

        if (isolated) {
            // Populate Isolation Fields
            if (details) {
                setIsolationCage(details.isolation_cage || data.cage_no || ''); // Fallback to current cage
                setIsolationDuration(Number(details.isolation_duration) || 0);
                setHasPostIsolation(!!details.has_post_isolation);
                setPostIsolationCage(details.post_cage || '');
                setPostIsolationDuration(Number(details.post_duration) || 0);
            } else {
                // If no details but isolated, infer from summary fields
                setIsolationCage(data.cage_no || '');
                setIsolationDuration(Number(data.length_of_stay_days) || 0);
            }
        } else {
            // Populate Standard Fields
            setStandardCage(data.cage_no || '');
            setStandardDuration(Number(data.length_of_stay_days) || 0);
        }
    };

    // --- Saving ---
    useImperativeHandle(ref, () => ({
        save: async () => handleSave()
    }));

    const handleSave = async (silent = false) => {
        try {
            setSaving(true);

            // Prepare Payload
            const payload = {
                is_isolated: isIsolated ? 1 : 0,
                accommodation_details: null // Default
            };

            if (isIsolated) {
                // ISOLATED MODE logic
                // 1. Calculate Total Summary Duration
                const isoDur = Number(isolationDuration) || 0;
                const postDur = hasPostIsolation ? (Number(postIsolationDuration) || 0) : 0;
                payload.length_of_stay_days = isoDur + postDur;

                // 2. Determine Current Active Cage
                // If in isolation (assumed start), active cage is isolation cage.
                // NOTE: We don't historically track "when" isolation started here, so we assume strictly:
                // If isolated => Current Cage = Isolation Cage.
                payload.cage_no = isolationCage;

                // 3. Save Details
                payload.accommodation_details = {
                    isolation_cage: isolationCage,
                    isolation_duration: isoDur,
                    has_post_isolation: hasPostIsolation,
                    post_cage: hasPostIsolation ? postIsolationCage : null,
                    post_duration: hasPostIsolation ? postDur : 0
                };
            } else {
                // STANDARD MODE logic
                payload.length_of_stay_days = Number(standardDuration) || 0;
                payload.cage_no = standardCage;
                // We can clear details or leave them null
                payload.accommodation_details = null;
            }

            await inpatientAPI.updateInpatientVisit(inpatientId, payload);
            if (!silent) {
                showSuccessToast(t('common.saveSuccess'));
            }
            if (onSave) {
                onSave();
            }
            return true;
        } catch (error) {
            console.error(error);
            if (!silent) {
                showErrorToast(t('common.saveError'));
            }
            return false;
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: '#6B7280' }}>
            <div className="spinner" style={{ width: '24px', height: '24px', border: '3px solid #E5E7EB', borderTopColor: '#00D4B3', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '10px' }}></div>
            {t('common.loading')}
        </div>
    );

    const inputStyle = {
        width: '100%',
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        fontSize: '14px',
        color: '#1F2937',
        backgroundColor: '#F9FAFB',
        outline: 'none',
        transition: 'all 0.2s ease'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px',
        marginLeft: '4px'
    };

    const sectionStyle = {
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #F3F4F6',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02)'
    };

    return (
        <div className="accommodation-content tab-content-inner" style={{ maxWidth: '800px', margin: '0 auto' }}>

            {/* Header / Toggle Section */}
            <div style={{
                ...sectionStyle,
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: isIsolated ? '1px solid #FEF2F2' : '1px solid #F3F4F6',
                background: isIsolated ? '#FFF1F2' : '#FFFFFF'
            }}>
                <div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: isIsolated ? '#991B1B' : '#111827', margin: 0 }}>
                        {t('receptionInpatients.details.accommodation.isolationStatus.title', { defaultValue: 'Isolation Status' })}
                    </h3>
                    <p style={{ fontSize: '14px', color: isIsolated ? '#B91C1C' : '#6B7280', margin: '4px 0 0' }}>
                        {isIsolated
                            ? t('receptionInpatients.details.accommodation.isolationStatus.isolated', { defaultValue: 'Patient is currently strictly isolated.' })
                            : t('receptionInpatients.details.accommodation.isolationStatus.standard', { defaultValue: 'Standard accommodation (Non-Isolated).' })
                        }
                    </p>
                </div>

                <div
                    onClick={() => setIsIsolated(!isIsolated)}
                    style={{
                        position: 'relative',
                        width: '64px',
                        height: '36px',
                        borderRadius: '999px',
                        cursor: 'pointer',
                        backgroundColor: isIsolated ? '#EF4444' : '#E5E7EB',
                        transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    <div style={{
                        position: 'absolute',
                        top: '4px',
                        left: isIsolated ? '32px' : '4px',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        {isIsolated ? (
                            <i className="fas fa-exclamation" style={{ color: '#EF4444', fontSize: '12px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}></i>
                        ) : (
                            <i className="fas fa-paw" style={{ color: '#9CA3AF', fontSize: '12px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}></i>
                        )}
                    </div>
                </div>
            </div>

            {/* Dynamic Content */}
            <div style={{
                ...sectionStyle,
                border: isIsolated ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative Background Icon */}
                <i className={`fas ${isIsolated ? 'fa-user-shield' : 'fa-home'}`} style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    fontSize: '120px',
                    opacity: '0.05',
                    color: isIsolated ? '#EF4444' : '#111827',
                    pointerEvents: 'none'
                }}></i>

                {!isIsolated ? (
                    // STANDARD FORM
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', position: 'relative' }}>
                        <div>
                            <label style={labelStyle}>{t('receptionInpatients.admit.fields.cage.label', { defaultValue: 'Cage Number' })}</label>
                            <div style={{ position: 'relative' }}>
                                <i className="fas fa-door-open" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}></i>
                                <input
                                    type="text"
                                    value={standardCage}
                                    onChange={(e) => setStandardCage(e.target.value)}
                                    placeholder={t('receptionInpatients.admit.fields.cage.placeholder', { defaultValue: 'Enter cage number...' })}
                                    style={{ ...inputStyle, paddingLeft: '44px', borderColor: standardCage ? '#10B981' : '#E5E7EB' }}
                                    onFocus={(e) => e.target.style.borderColor = '#10B981'} // Teal
                                    onBlur={(e) => { e.target.style.borderColor = standardCage ? '#10B981' : '#E5E7EB'; handleSave(true); }}
                                />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('receptionInpatients.admit.fields.lengthOfStay.label', { defaultValue: 'Length of Stay (days)' })}</label>
                            <div style={{ position: 'relative' }}>
                                <i className="fas fa-clock" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}></i>
                                <input
                                    type="number"
                                    value={standardDuration}
                                    onChange={(e) => setStandardDuration(e.target.value)}
                                    placeholder={t('receptionInpatients.admit.fields.lengthOfStay.placeholder', { defaultValue: 'e.g., 3' })}
                                    style={{ ...inputStyle, paddingLeft: '44px' }}
                                    onFocus={(e) => e.target.style.borderColor = '#3B82F6'} // Blue
                                    onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; handleSave(true); }}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    // ISOLATED FORM
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                            <div>
                                <label style={{ ...labelStyle, color: '#991B1B' }}>{t('receptionInpatients.details.accommodation.isolationCage', { defaultValue: 'Isolation Cage' })}</label>
                                <div style={{ position: 'relative' }}>
                                    <i className="fas fa-dungeon" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#EF4444' }}></i>
                                    <input
                                        type="text"
                                        value={isolationCage}
                                        onChange={(e) => setIsolationCage(e.target.value)}
                                        style={{ ...inputStyle, paddingLeft: '44px', borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }}
                                        onFocus={(e) => { e.target.style.borderColor = '#EF4444'; e.target.style.boxShadow = '0 0 0 4px rgba(239, 68, 68, 0.1)'; }}
                                        onBlur={(e) => { e.target.style.borderColor = '#FCA5A5'; e.target.style.boxShadow = 'none'; handleSave(true); }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ ...labelStyle, color: '#991B1B' }}>{t('receptionInpatients.details.accommodation.isolationDuration', { defaultValue: 'Isolation Duration (Days)' })}</label>
                                <div style={{ position: 'relative' }}>
                                    <i className="fas fa-hourglass-half" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#EF4444' }}></i>
                                    <input
                                        type="number"
                                        value={isolationDuration}
                                        onChange={(e) => setIsolationDuration(e.target.value)}
                                        style={{ ...inputStyle, paddingLeft: '44px', borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }}
                                        onFocus={(e) => { e.target.style.borderColor = '#EF4444'; e.target.style.boxShadow = '0 0 0 4px rgba(239, 68, 68, 0.1)'; }}
                                        onBlur={(e) => { e.target.style.borderColor = '#FCA5A5'; e.target.style.boxShadow = 'none'; handleSave(true); }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Post Isolation Divider */}
                        <div style={{
                            borderTop: '2px dashed #FECACA',
                            paddingTop: '24px',
                            marginTop: '24px',
                            position: 'relative'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '-12px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#FFFFFF',
                                padding: '0 12px',
                                color: '#EF4444',
                                fontWeight: '600',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                {t('receptionInpatients.details.accommodation.nextSteps', { defaultValue: 'Next Steps' })}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: hasPostIsolation ? '24px' : '0' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '6px',
                                        border: hasPostIsolation ? '2px solid #EF4444' : '2px solid #D1D5DB',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px',
                                        backgroundColor: hasPostIsolation ? '#EF4444' : 'transparent',
                                        transition: 'all 0.2s'
                                    }}>
                                        {hasPostIsolation && <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }}></i>}
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={hasPostIsolation}
                                        onChange={(e) => setHasPostIsolation(e.target.checked)}
                                        style={{ display: 'none' }}
                                    />
                                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#1F2937' }}>
                                        {t('receptionInpatients.details.accommodation.continueAfterIsolation', { defaultValue: 'Continue stay after isolation?' })}
                                    </span>
                                </label>
                            </div>

                            {hasPostIsolation && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', animation: 'fadeIn 0.3s' }}>
                                    <div>
                                        <label style={labelStyle}>{t('receptionInpatients.details.accommodation.postIsolationCage', { defaultValue: 'Post-Isolation Cage' })}</label>
                                        <div style={{ position: 'relative' }}>
                                            <i className="fas fa-door-open" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}></i>
                                            <input
                                                type="text"
                                                value={postIsolationCage}
                                                onChange={(e) => setPostIsolationCage(e.target.value)}
                                                style={{ ...inputStyle, paddingLeft: '44px' }}
                                                onFocus={(e) => e.target.style.borderColor = '#10B981'}
                                                onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; handleSave(true); }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>{t('receptionInpatients.details.accommodation.additionalDuration', { defaultValue: 'Additional Duration (Days)' })}</label>
                                        <div style={{ position: 'relative' }}>
                                            <i className="fas fa-plus-circle" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}></i>
                                            <input
                                                type="number"
                                                value={postIsolationDuration}
                                                onChange={(e) => setPostIsolationDuration(e.target.value)}
                                                style={{ ...inputStyle, paddingLeft: '44px' }}
                                                onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                                                onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; handleSave(true); }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default AccommodationTab;
